import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, DollarSign, CheckCircle, AlertCircle, XCircle, Clock, Filter, Search } from 'lucide-react';
import { invoicesAPI, Invoice } from '../../services/invoicesAPI';

interface InvoiceViewProps {
  siteId: string;
}

const InvoiceView: React.FC<InvoiceViewProps> = ({ siteId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    loadInvoices();
  }, [siteId]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await invoicesAPI.getSiteInvoices(siteId, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        year: yearFilter !== 'all' ? parseInt(yearFilter) : undefined
      });
      setInvoices(response.data.invoices);
    } catch (error: any) {
      console.error('Invoice load error:', error);
      if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view invoices for this site.');
      } else {
        setError(error.response?.data?.error || 'Failed to load invoices');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      // In a real implementation, this would generate and download a PDF
      const invoiceData = {
        invoiceNumber: invoice.invoiceNumber,
        billingPeriod: invoice.billingPeriod,
        items: invoice.items,
        subtotal: invoice.subtotal,
        taxAmount: invoice.taxAmount,
        taxRate: invoice.taxRate,
        totalAmount: invoice.totalAmount,
        billingInfo: invoice.billingInfo,
        companyInfo: invoice.companyInfo,
        status: invoice.status,
        createdAt: invoice.createdAt
      };

      // Create a simple text representation for now
      const invoiceText = `
INVOICE: ${invoice.invoiceNumber}
Date: ${new Date(invoice.createdAt).toLocaleDateString()}
Billing Period: ${new Date(invoice.billingPeriod.startDate).toLocaleDateString()} - ${new Date(invoice.billingPeriod.endDate).toLocaleDateString()}

BILLING TO:
${invoice.billingInfo.companyName}
${invoice.billingInfo.billingAddress.street}
${invoice.billingInfo.billingAddress.city}, ${invoice.billingInfo.billingAddress.postcode}
${invoice.billingInfo.billingAddress.country}
Email: ${invoice.billingInfo.contactEmail}
Phone: ${invoice.billingInfo.contactPhone}

ITEMS:
${invoice.items.map(item => `${item.description} - ${item.quantity} x £${item.unitPrice.toFixed(2)} = £${item.amount.toFixed(2)}`).join('\n')}

SUBTOTAL: £${invoice.subtotal.toFixed(2)}
TAX (${invoice.taxRate}%): £${invoice.taxAmount.toFixed(2)}
TOTAL: £${invoice.totalAmount.toFixed(2)}

STATUS: ${invoice.status.toUpperCase()}
${invoice.paidDate ? `PAID DATE: ${new Date(invoice.paidDate).toLocaleDateString()}` : ''}
${invoice.dueDate ? `DUE DATE: ${new Date(invoice.dueDate).toLocaleDateString()}` : ''}

---
Generated by Laundrette Management System
      `.trim();

      // Create and download file
      const blob = new Blob([invoiceText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Failed to download invoice');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'sent':
        return <Clock className="w-4 h-4" />;
      case 'draft':
        return <FileText className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.billingInfo.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    const matchesYear = yearFilter === 'all' || 
                       new Date(invoice.createdAt).getFullYear().toString() === yearFilter;
    
    return matchesSearch && matchesStatus && matchesYear;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAvailableYears = () => {
    const years = new Set(invoices.map(inv => new Date(inv.createdAt).getFullYear().toString()));
    return Array.from(years).sort((a, b) => parseInt(b) - parseInt(a));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Billing & Invoices</h2>
          <p className="text-gray-600">View and download your invoices</p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="sent">Sent</option>
              <option value="draft">Draft</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Year Filter */}
          <div className="sm:w-32">
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Years</option>
              {getAvailableYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoices Found</h3>
          <p className="text-gray-600">
            {invoices.length === 0 
              ? "You don't have any invoices yet. Invoices will appear here once billing is active."
              : "No invoices match your current filters."
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(invoice.createdAt)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(invoice.billingPeriod.startDate)} - {formatDate(invoice.billingPeriod.endDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        £{invoice.totalAmount.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {invoice.items.length} item{invoice.items.length !== 1 ? 's' : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        <span className="ml-1 capitalize">{invoice.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
                      </div>
                      {invoice.paidDate && (
                        <div className="text-sm text-green-600">
                          Paid {formatDate(invoice.paidDate)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDownloadInvoice(invoice)}
                        className="text-primary-600 hover:text-primary-900 inline-flex items-center space-x-1"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Invoice {selectedInvoice.invoiceNumber}
                </h3>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Invoice Details */}
              <div className="space-y-6">
                {/* Billing Info */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Bill To</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p className="font-medium text-gray-900">{selectedInvoice.billingInfo.companyName}</p>
                      <p>{selectedInvoice.billingInfo.billingAddress.street}</p>
                      <p>{selectedInvoice.billingInfo.billingAddress.city}, {selectedInvoice.billingInfo.billingAddress.postcode}</p>
                      <p>{selectedInvoice.billingInfo.billingAddress.country}</p>
                      <p>{selectedInvoice.billingInfo.contactEmail}</p>
                      <p>{selectedInvoice.billingInfo.contactPhone}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Invoice Details</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Invoice Number: {selectedInvoice.invoiceNumber}</p>
                      <p>Invoice Date: {formatDate(selectedInvoice.createdAt)}</p>
                      <p>Period: {formatDate(selectedInvoice.billingPeriod.startDate)} - {formatDate(selectedInvoice.billingPeriod.endDate)}</p>
                      <p>Due Date: {selectedInvoice.dueDate ? formatDate(selectedInvoice.dueDate) : 'N/A'}</p>
                      <p>Status: <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${getStatusColor(selectedInvoice.status)}`}>{selectedInvoice.status}</span></p>
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Items</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedInvoice.items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">£{item.unitPrice.toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">£{item.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={3} className="px-4 py-3 text-sm font-medium text-gray-900">Subtotal</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">£{selectedInvoice.subtotal.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="px-4 py-3 text-sm font-medium text-gray-900">Tax ({selectedInvoice.taxRate}%)</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">£{selectedInvoice.taxAmount.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="px-4 py-3 text-base font-bold text-gray-900">Total</td>
                          <td className="px-4 py-3 text-base font-bold text-primary-600 text-right">£{selectedInvoice.totalAmount.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => handleDownloadInvoice(selectedInvoice)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceView;
